import type { GetCurrentMemberFn, SearchMembersFn } from '@/features/iam'
import { type Job } from '@/features/jobs/Job'
import { action, computed, observable } from 'mobx'
import { required, validationContext } from 'validx'
import { CursorPaged, type CursorPageQuery } from '@/infra/paging'
import { type Member } from '@/features/iam/members/Member'
import { Id } from '@/infra/id'
import { type ProviderStore } from '@/features/jobs/ProviderStore'

/**
 * Picks the Pro a job is transferred to.
 */
export class ProInputEditorViewModel {
  /**
   * The Pro the jobs are transferred to.
   */
  @observable
  accessor pro: Member | null | undefined = undefined

  /**
   * The jobs being transferred.
   */
  @observable
  accessor jobs: Job[] = []

  /**
   * Whether we can display all pros, rather than only qualified ones.
   */
  @observable
  accessor canDisplayAllPros = false

  /**
   * Validation context for pro.
   */
  readonly validation = validationContext(this, {
    pro: [required('Pro is required')],
  })

  constructor(
    private readonly _findMembers: SearchMembersFn,
    private readonly _providerStore: ProviderStore,
    private readonly _getCurrentMember: GetCurrentMemberFn,
  ) {}

  /**
   * Whether we can display only qualified pros.
   */
  @computed
  get canDisplayQualifiedPros() {
    return !this.canDisplayAllPros
  }

  /**
   * The item source the Pro search draws from, depending on the filter.
   */
  @computed
  get proItemSource() {
    return this.canDisplayAllPros ? this.fetchPros : this.fetchQualifiedPros
  }

  /**
   * The Pro search placeholder, depending on the filter.
   */
  @computed
  get proPlaceholder() {
    return this.canDisplayAllPros
      ? 'Search any available'
      : 'Search qualified only'
  }

  /**
   * Getter for the current job champion pro.
   */
  @computed
  get currentChampionPro() {
    return this.jobs[0].championProvider?.member
  }

  /**
   * Sets dropdown to show all pros.
   */
  @action.bound
  setDisplayAllPros() {
    this.canDisplayAllPros = true
  }

  /**
   * Sets dropdown to show only qualifed pros.
   */
  @action.bound
  setDisplayQualifiedPros() {
    this.canDisplayAllPros = false
  }

  /**
   * Sets pro to transfer.
   */
  @action.bound
  setPro(pro?: Member | null) {
    this.pro = pro
  }

  /**
   * Indicates if pro is valid.
   */
  @action.bound
  validate() {
    return this.validation.reset().validate().isValid
  }

  /**
   * Clears the fields of the View Model.
   */
  @action.bound
  reset() {
    this.validation.reset()
    this.pro = undefined
    this.canDisplayAllPros = false
  }

  /**
   * Sets the jobs being transferred.
   */
  @action.bound
  setJobs(jobs: Job[]) {
    this.jobs = jobs
  }

  /**
   * Fetches pros.
   */
  fetchPros = async (
    searchText: string,
    params: Partial<CursorPageQuery>,
    signal?: AbortSignal,
  ): Promise<CursorPaged<Member>> => {
    const collaborators = this.jobs.map((j) => j.collaborators.items).flat()
    const paged = await this._findMembers(
      {
        after: params.after,
        limit: params.limit,
        workspaceId: Id.assert(
          this._getCurrentMember(this.jobs[0].workspace.id).workspace.id,
        ),
        searchText: searchText,
      },
      signal,
    )

    return CursorPaged.map(paged, (data) =>
      data.filter((m) => {
        const invalidCandidate =
          collaborators.some((s) => s.member === m) ||
          m.user.id === 'SYSTEM_ROBOT'
        return !invalidCandidate
      }),
    )
  }

  /**
   * Fetches qualified pros.
   */
  fetchQualifiedPros = async (
    searchText: string,
    query: Partial<CursorPageQuery>,
    signal?: AbortSignal,
  ): Promise<CursorPaged<Member | null>> => {
    const result = await Promise.all(
      this.jobs.map((j) =>
        this._providerStore.listQualifiedProviders(
          {
            job: j,
            searchText,
            after: query.after ?? undefined,
          },
          signal,
        ),
      ),
    )

    // because we support multiple jobs transfer when fetching qualified pros we want to display only the pros
    // that are present in all the selected jobs
    return CursorPaged.of(
      result
        .map((i) => i.data)
        .reduce((previousValue, currentValue) =>
          previousValue.filter((item) =>
            currentValue.some(
              (pro) => pro?.user.publicId === item?.user.publicId,
            ),
          ),
        ),
    )
  }
}
